import { Model } from '@/services/api/lib'

class Product extends Model {
  resource () {
    return 'catalog/v1/products'
  }

  images () {
    this.hasMany(Image)
  }

  updateMethod () {
    return 'PATCH'
  }

  save () {
    if (Array.isArray(this.application) && this.application.length === 0) {
      this.application = null
    }

    if (this.tiers && this.tiers.length) {
      this.tiers = this.tiers.filter(t => t.price.amount && t.gross_price.amount)
    }

    return super.save()
  }

  deleteOption (option) {
    return this.request({
      method: 'DELETE',
      url: `${this.endpoint()}/variants`,
      data: { variants: [{ key: option.key }] }
    }).then(response => response.data)
  }

  restock (quantity) {
    quantity = parseFloat(quantity)
    return this.request({
      method: 'PATCH',
      url: `${this.endpoint()}/restock`,
      data: { quantity: quantity }
    }).then(() => {
      if (this.inventory.track) {
        this.inventory.quantity += quantity
        this.$['inventory']['quantity'] = this.inventory.quantity
      }
    })
  }

  deduct (quantity) {
    quantity = parseFloat(quantity)
    return this.request({
      method: 'PATCH',
      url: `${this.endpoint()}/deduct`,
      data: { quantity: quantity }
    }).then(() => {
      if (!this.inventory.track) {
        return
      }
      if (quantity <= this.inventory.quantity) {
        this.inventory.quantity -= quantity
        this.$['inventory']['quantity'] = this.inventory.quantity
        return
      }
      this.inventory.quantity = 0
      this.$['inventory']['quantity'] = this.inventory.quantity
    })
  }

  doExport (options) {
    let data = null
    if (options) {
      data = options
    }

    return this.request({
      method: 'POST',
      url: `${this.endpoint()}/export`,
      data: data
    })
  }

  doImport (data) {
    return this.request({
      method: 'POST',
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      url: `${this.endpoint()}/import`,
      data: data
    })
  }

  reindex (ids) {
    let data = null
    if (ids && ids.length > 0) {
      data = {
        product_ids: ids
      }
    }
    return this.request({
      method: 'POST',
      url: `${this.endpoint()}/reindex`,
      data: data
    })
  }

  createVariants (variants) {
    return this.requestVariants('POST', {
      variants,
      translations: this.translations
    })
  }

  updateVariants (variants) {
    return this.requestVariants('PUT', {
      variants,
      translations: this.translations
    })
  }

  requestVariants (method, data) {
    return new Promise(async (resolve, reject) => {
      this.request({
        method,
        url: `${this.endpoint()}/variants`,
        data
      }).then(res => {
        this.children = res.data.children
        this.variants = res.data.variants
        resolve()
      }).catch(e => {
        reject(e)
      })
    })
  }

  updateVariantsAttributes (data) {
    return new Promise(async (resolve, reject) => {
      this.request({
        method: 'PUT',
        url: `${this.endpoint()}/variants/attributes`,
        data
      }).then(res => {
        this.children = res.data.children
        resolve()
      }).catch(e => {
        reject(e)
      })
    })
  }

  defaults () {
    return {
      sku: '',
      type: 'physical',
      classification: 'common',
      components: [],
      name: '',
      brand: '',
      description: '',
      price: null,
      sale_price: null,
      gross_price: null,
      gross_sale_price: null,
      image_url: undefined,
      inventory: {
        policy: 'stop',
        track: true,
        is_available: true,
        is_restockable: true,
        is_bundle_exclusive: false,
        quantity: 0,
        min_per_order: null,
        max_per_order: null
      },
      vat: null,
      tags: [],
      taxonomy_id: '',
      taxonomy: {},
      variants: [],
      properties: [],
      extras: [],
      images: [],
      application: null,
      order: 0,
      related: {
        products: [],
        taxonomy: []
      },
      translations: null,
      customer_group: [],
      tiers: []
    }
  }

  validations () {
    return {
      'sku': 'string',
      'type': 'required',
      'classification': 'required',
      'name': 'required|string',
      'brand': 'required|string',
      'order': 'number',
      'slug': 'string',
      'description': 'string',
      'price': 'object',
      'price.amount': 'required|number|min:0',
      'price.currency': 'required|string',
      'sales_price': 'object',
      'sales_price.amount': 'number|min:0',
      'sales_price.currency': 'string',
      'image_url': 'required|string',
      'inventory': 'required|object',
      'inventory.policy': 'required|string',
      'inventory.track': 'required|boolean',
      'inventory.is_available': 'required|boolean',
      'inventory.is_restockable': 'required|boolean',
      'inventory.is_bundle_exclusive': 'boolean',
      'inventory.quantity': 'required|number|min:0',
      'inventory.min_per_order': 'number|min:0',
      'inventory.max_per_order': 'number|min:0',
      'tags': 'array',
      'taxonomy_id': 'string',
      'properties': 'array',
      'properties.*.name': 'required|string',
      'properties.*.value': 'required',
      'customer_group': 'array'
    }
  }
}

class VariantOption extends Model {
  resource () {
    return 'variants'
  }

  validations () {
    return {
      'name': 'required|string',
      'options': 'required|array'
    }
  }

  generate () {
    return this._create()
  }
}

class Taxonomy extends Model {
  resource () {
    return 'catalog/v1/taxonomy'
  }

  defaults () {
    return {
      name: '',
      parent_id: null,
      image_url: null,
      children_ids: [],
      product_ids: [],
      application: null,
      order: 0,
      translations: null
    }
  }

  updateMethod () {
    return 'PATCH'
  }

  save () {
    if (Array.isArray(this.application) && this.application.length === 0) {
      this.application = null
    }

    return super.save()
  }
}

class Image extends Model {
  resource () {
    return 'images'
  }

  validations () {
    return {
      'url': 'required|url',
      'title': 'string',
      'alt': 'string',
      'default': 'boolean',
      'sort_order': 'integer'
    }
  }
}

export default {
  Product,
  Taxonomy,
  VariantOption,
  Image
}
